import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { ComboBoxOption } from '../types/ComboBoxOption';

export const useVisibleComboBoxTags = <Option extends ComboBoxOption>(selectedOptions: Option[]) => {
    const [visibleTagCount, setVisibleTagCount] = useState(1);
    const [layoutVersion, setLayoutVersion] = useState(0);
    const triggerValueRef = useRef<HTMLDivElement>(null);
    const triggerTagsRef = useRef<HTMLDivElement>(null);
    const measuredCountRef = useRef<HTMLDivElement>(null);
    const measuredTagRefs = useRef<Array<HTMLDivElement | null>>([]);

    useLayoutEffect(() => {
        if (selectedOptions.length === 0) {
            setVisibleTagCount(0);
            return;
        }

        const availableWidth = triggerValueRef.current?.getBoundingClientRect().width ?? 0;
        const countWidth = measuredCountRef.current?.getBoundingClientRect().width ?? 0;
        const tagWidths = selectedOptions.map(
            (_, index) => measuredTagRefs.current[index]?.getBoundingClientRect().width ?? 0
        );

        if (availableWidth <= 0 || tagWidths.every((tagWidth) => tagWidth <= 0)) {
            setVisibleTagCount(1);
            return;
        }

        for (
            let candidateVisibleCount = selectedOptions.length;
            candidateVisibleCount >= 1;
            candidateVisibleCount -= 1
        ) {
            const firstVisibleIndex = selectedOptions.length - candidateVisibleCount;
            const hiddenCount = selectedOptions.length - candidateVisibleCount;
            const visibleTagWidths = tagWidths.slice(firstVisibleIndex);
            const tagGapWidth = Math.max(visibleTagWidths.length - 1, 0) * 4;
            const countWidthWithGap = hiddenCount > 0 ? countWidth + 4 : 0;
            const usedWidth =
                visibleTagWidths.reduce((totalWidth, tagWidth) => totalWidth + tagWidth, 0) +
                tagGapWidth +
                countWidthWithGap;

            if (usedWidth <= availableWidth) {
                setVisibleTagCount(candidateVisibleCount);
                return;
            }
        }

        setVisibleTagCount(1);
    }, [selectedOptions, layoutVersion]);

    useEffect(() => {
        if (!triggerValueRef.current || typeof ResizeObserver === 'undefined') {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            setLayoutVersion((currentLayoutVersion) => currentLayoutVersion + 1);
        });

        resizeObserver.observe(triggerValueRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return {
        measuredCountRef,
        measuredTagRefs,
        triggerTagsRef,
        triggerValueRef,
        visibleTagCount,
    };
};
